import { useCallback, useEffect, useRef, useState } from 'react';
import { relockPaid, unlockPaid } from '../engine/runtime';
import { track } from '../lib/events';
import type { AccountUser } from '../lib/account';

/**
 * Whether this browser has been given the paid half of the course.
 *
 * The question is asked of the Worker, not of the progress record: what a
 * learner has cleared and what a learner has bought are different facts kept
 * in different places, and only one of them is the tab's to decide. A refusal
 * is a state rather than an error — 401 means nobody is signed in and 402
 * means this account has not bought it, and the page has something different
 * to say for each.
 *
 * Asked on load, and again whenever the signed-in user changes, which is what
 * makes a purchase take effect: sign in on a second device and the levels
 * arrive without a reload. `refresh` is the same call by hand, for the moment
 * a payment returns.
 *
 * It runs in both directions. The same account change that folds the paid
 * half in when somebody signs in has to take it back out when they sign out,
 * and for a long time it did not: the levels were folded into the runtime's
 * catalogue in place, nothing ever put the stubs back, and a signed-out tab
 * went on holding a course it had no claim to. The visible cost was the offer
 * disappearing — the block asks the catalogue what is locked, and the answer
 * was "nothing" (lib/entitlement.ts) — so the one screen that could have sold
 * the course to somebody who had just proved they did not own it was the one
 * screen that would not draw. `relockPaid` is that other direction.
 */
export type PaidState =
  /** Not asked yet. The map draws the paid half locked until we know. */
  | 'unknown'
  /** Nobody is signed in, so there is no account to own anything. */
  | 'anonymous'
  /** Signed in, has not bought the course. */
  | 'unowned'
  /** Signed in and paid; the catalogue now holds all fifty levels. */
  | 'owned'
  /** No Worker reachable — offline, or a bundle served without one. */
  | 'unavailable';

export interface PaidApi {
  state: PaidState;
  /** The one thing most callers want. */
  owned: boolean;
  refresh(): Promise<void>;
}

/**
 * `onCatalogue` fires whenever levels were actually folded into the catalogue
 * or taken back out of it, and its job is to make the caller re-read it:
 * `unlockPaid` and `relockPaid` both mutate the `Map` in place, so React has
 * no idea anything changed. It does not fire when nothing moved, which is
 * every ordinary load: the catalogue arrives locked and stays that way.
 */
export function usePaid(user: AccountUser | null, onCatalogue: () => void): PaidApi {
  const [state, setState] = useState<PaidState>('unknown');
  const onCatalogueRef = useRef(onCatalogue);
  onCatalogueRef.current = onCatalogue;
  /**
   * So that owning the course is counted once per page load rather than once
   * per refresh. `refresh` runs on every account change and again when a
   * payment returns, and a funnel in which one purchase appears four times is
   * a funnel that cannot be divided by anything (docs/analytics.md).
   */
  const counted = useRef(false);

  const refresh = useCallback(async () => {
    let result;
    try {
      result = await unlockPaid();
    } catch {
      // A malformed answer from the Worker is a broken deployment, not a
      // reason to take the free half away from someone who is reading it.
      setState('unavailable');
      return;
    }
    if (result.state === 'unlocked') {
      setState('owned');
      if (!counted.current) {
        counted.current = true;
        track('paid');
      }
      // Even when nothing was added — a second call for an account that
      // already had them — the caller is told, because the cheap re-read is
      // better than a map that is right only on the first try.
      onCatalogueRef.current();
      return;
    }
    setState(result.state);
    // A refusal the Worker actually gave: nobody is signed in, or this
    // account has not bought the course. Either way the levels this tab may
    // be holding are not its own, and they go back to being stubs.
    //
    // `unavailable` is deliberately not in this list. It means the question
    // could not be asked — offline, or no Worker in front of the page — and
    // taking the paid half away from somebody mid-level because their train
    // went into a tunnel is the one failure here that is worse than the
    // staleness it would be fixing.
    if (result.state === 'anonymous' || result.state === 'unowned') {
      try {
        if ((await relockPaid()) > 0) onCatalogueRef.current();
      } catch {
        // The catalogue never loaded, which is a boot failure the page is
        // already showing. Nothing here to say a second time.
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Keyed on the id rather than the object: `useAccount` replaces the user
    // object on every refresh, and re-fetching the whole paid half each time
    // the account is re-read would be thirty levels down the wire for nothing.
  }, [user?.id, refresh]);

  return { state, owned: state === 'owned', refresh };
}
